import {
  Dispatch, SetStateAction, useCallback, useState,
} from 'react';
import { upperFirst } from 'lodash';
import {
  useHeroSnackbar,
  useTable,
  useQueryTableFetch,
  useTranslations,
  useTableLocalization,
  useFilterButton,
  useTablePreserverActivePageValidator,
  useTablePreserverDecoder,
  useTablePreserverEncoder,
  usePubSub,
  buildTablePreserverDecoderConfigItem,
  TablePreserverCommonInitialTableValues,
  IUseTableReturn,
  IUseTableResetTableMethodsReturn,
  IUseQueryTableFetchReturn,
  IUseTableLocalizationReturn,
  IUseFilterButtonReturn,
  TUseTablePreserverDecoderConfigs,
  TablePreserverDecoderConfigItemTypeEnum,
  TSetSearchParams,
  TSecurityScoreName,
  getSecurityScoreServicesAndEmployeesStrength,
  getUniqkeyAccessScoreStrength,
} from '@uniqkey-frontend/shared-app';
import {
  ActivityFilter,
  EmployeeAccountOrderV2Property,
  EmployeeAccountStatus,
  EmployeeAccountType,
} from '@uniqkey-backend-organization-web/api-client';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import PubSubEventEnum from '../../../enums/PubSubEventEnum';
import ReactQueryKeyEnum from '../../../enums/ReactQueryKeyEnum';
import {
  IGetEmployeeAccountsParams,
  TGetEmployeeAccountsMethod,
} from '../../useEmployeeAccountsAPI/interfaces';
import {
  IEmployeesListFilterSubmitResult,
} from '../../../pages/EmployeesPage/components/EmployeesListFilter';
import { IEmployeesTableRow } from '../../../components/tables/EmployeesTable';
import useEmployeeAccountsAPI from '../../useEmployeeAccountsAPI';

export const REACT_QUERY_EMPLOYEES_KEY = [ReactQueryKeyEnum.Employees];

interface IPersistentFilters extends Pick<IGetEmployeeAccountsParams, 'groupId'> {}

export interface IUseEmployeesTableParams {
  persistentFilters?: IPersistentFilters;
  noDataMessageKey: string;
}

export interface IUseEmployeesTableReturn extends
  Omit<IUseTableReturn<IEmployeesTableRow>, 'selectedRows' | 'resetTableMethods'>,
  Pick<IUseTableResetTableMethodsReturn, 'resetActivePage' | 'resetSelectedRows'>,
  Omit<IUseQueryTableFetchReturn<TGetEmployeeAccountsMethod>, 'data' | 'isFetchedAfterMount'>,
  IUseFilterButtonReturn
{
  employees: IUseQueryTableFetchReturn<TGetEmployeeAccountsMethod>['data'];
  selectedEmployees: IUseTableReturn<IEmployeesTableRow>['selectedRows'];
  localization: IUseTableLocalizationReturn['localization'];
  searchQuery: IGetEmployeeAccountsParams['searchQuery'];
  setSearchQuery: Dispatch<SetStateAction<IGetEmployeeAccountsParams['searchQuery']>>;
  filterValues: IEmployeesListFilterSubmitResult;
  setFilterValues: Dispatch<SetStateAction<IEmployeesListFilterSubmitResult>>;
}

interface ITablePreservationConfigs extends
  Omit<IGetEmployeeAccountsParams,
    'page' | 'pageLength' | 'groupId' | 'orderPropertyName' | 'isDescending'
    | 'showSupporters' | 'showProcessingUsers' | 'maxSecurityScore' | 'minSecurityScore'
    | 'minUniqkeyAccessScore' | 'maxUniqkeyAccessScore' | 'isUniqkeyAccessScoreEmpty'
    | 'showUnprocessedUsers' | 'isSecurityScoreEmpty'
  >,
  Pick<IUseTableReturn<IEmployeesTableRow>, 'columnOrderBy' | 'columnOrderDirection'>
{
  activePage: IGetEmployeeAccountsParams['page'];
  perPage: IGetEmployeeAccountsParams['pageLength'];
  securityScoreName: TSecurityScoreName;
  uniqkeyAccessScoreName: TSecurityScoreName;
  group: IEmployeesListFilterSubmitResult['group'];
}

const PRESERVATION_CONFIGS: TUseTablePreserverDecoderConfigs<ITablePreservationConfigs> = {
  activePage: true,
  perPage: true,
  searchQuery: true,
  columnOrderBy: buildTablePreserverDecoderConfigItem(
    TablePreserverDecoderConfigItemTypeEnum.ENUM,
    { enumToCheck: EmployeeAccountOrderV2Property },
  ),
  columnOrderDirection: true,
  typeFilter: buildTablePreserverDecoderConfigItem(
    TablePreserverDecoderConfigItemTypeEnum.ENUM,
    { enumToCheck: EmployeeAccountType },
  ),
  statusFilter: buildTablePreserverDecoderConfigItem(
    TablePreserverDecoderConfigItemTypeEnum.ENUM,
    { enumToCheck: EmployeeAccountStatus },
  ),
  securityScoreName: true,
  uniqkeyAccessScoreName: true,
  activityFilter: buildTablePreserverDecoderConfigItem(
    TablePreserverDecoderConfigItemTypeEnum.ENUM,
    { enumToCheck: ActivityFilter },
  ),
  group: buildTablePreserverDecoderConfigItem(
    TablePreserverDecoderConfigItemTypeEnum.OBJECT,
  ),
};

const useEmployeesTable = (
  params: IUseEmployeesTableParams,
): IUseEmployeesTableReturn => {
  const {
    persistentFilters,
    noDataMessageKey,
  } = params;

  const queryClient = useQueryClient();
  const { t } = useTranslations();
  const { showError } = useHeroSnackbar();
  const { getEmployeeAccounts } = useEmployeeAccountsAPI();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleOnRequestError = useCallback(() => {
    showError({ text: t('common.somethingWentWrong') });
  }, [showError, t]);

  const {
    activePage: initialActivePage,
    perPage: initialPerPage,
    typeFilter: initialTypeFilter,
    statusFilter: initialStatusFilter,
    securityScoreName: initialSecurityScoreName,
    uniqkeyAccessScoreName: initialUniqkeyAccessScoreName,
    searchQuery: initialSearchQuery,
    columnOrderBy: initialColumnOrderBy,
    columnOrderDirection: initialColumnOrderDirection,
    activityFilter: initialActivityFilter,
    group: initialGroup,
  } = useTablePreserverDecoder<ITablePreservationConfigs>(searchParams, PRESERVATION_CONFIGS);

  const [searchQuery, setSearchQuery] = useState<
    IGetEmployeeAccountsParams['searchQuery']
  >(() => initialSearchQuery);
  const [filterValues, setFilterValues] = useState<
    IEmployeesListFilterSubmitResult
  >({
    typeFilter: initialTypeFilter,
    statusFilter: initialStatusFilter,
    securityScoreName: initialSecurityScoreName,
    uniqkeyAccessScoreName: initialUniqkeyAccessScoreName,
    activityFilter: initialActivityFilter,
    group: initialGroup,
  });

  const { isFilterActive, numberOfActiveFilters } = useFilterButton(filterValues);

  const {
    activePage,
    perPage,
    columnOrderBy,
    columnOrderDirection,
    selectedRows: selectedEmployees,
    resetTableMethods,
    ...restTableProps
  } = useTable<IEmployeesTableRow>({
    initialActivePage,
    initialPerPage,
    initialColumnOrderBy,
    initialColumnOrderDirection,
  });

  const {
    resetTable,
    resetActivePage,
    resetSelectedRows,
  } = resetTableMethods;

  const handleTableReset = useCallback(() => {
    resetTable();
    setSearchQuery(TablePreserverCommonInitialTableValues.searchQuery);
    setFilterValues({
      typeFilter: undefined,
      statusFilter: undefined,
      securityScoreName: TablePreserverCommonInitialTableValues.securityScoreName,
      uniqkeyAccessScoreName: TablePreserverCommonInitialTableValues.uniqkeyAccessScoreName,
      activityFilter: undefined,
      group: undefined,
    });
  }, [resetTable]);
  usePubSub(PubSubEventEnum.RESET_TABLE, handleTableReset);

  const {
    data: employees, isLoading, isFetchedAfterMount, total, resetQuery,
  } = useQueryTableFetch({
    queryKey: REACT_QUERY_EMPLOYEES_KEY,
    queryClient,
    request: getEmployeeAccounts,
    params: {
      page: activePage,
      pageLength: perPage,
      typeFilter: filterValues.typeFilter,
      statusFilter: filterValues.statusFilter,
      activityFilter: filterValues.activityFilter,
      groupId: filterValues.group?.value,
      ...getSecurityScoreServicesAndEmployeesStrength(filterValues.securityScoreName),
      ...getUniqkeyAccessScoreStrength(filterValues.uniqkeyAccessScoreName),
      searchQuery,
      orderPropertyName: EmployeeAccountOrderV2Property[
        upperFirst(columnOrderBy) as keyof typeof EmployeeAccountOrderV2Property
      ],
      isDescending: columnOrderDirection ? columnOrderDirection === 'desc' : undefined,
      ...persistentFilters,
    },
    onRequestError: handleOnRequestError,
  });

  const { localization } = useTableLocalization({
    searchQuery,
    isFilterActive,
    noDataMessageKey,
    isLoading,
  });

  useTablePreserverActivePageValidator({
    activePage, resetActivePage, perPage, total, isFetchedAfterMount,
  });

  useTablePreserverEncoder<ITablePreservationConfigs>(setSearchParams as TSetSearchParams, {
    activePage,
    perPage,
    searchQuery,
    columnOrderBy,
    columnOrderDirection,
    typeFilter: filterValues.typeFilter,
    statusFilter: filterValues.statusFilter,
    securityScoreName: filterValues.securityScoreName,
    uniqkeyAccessScoreName: filterValues.uniqkeyAccessScoreName,
    activityFilter: filterValues.activityFilter,
    group: {
      value: filterValues.group,
      parser: JSON.stringify,
    },
  });

  return {
    employees,
    isLoading,
    total,
    resetQuery,
    activePage,
    perPage,
    columnOrderBy,
    columnOrderDirection,
    selectedEmployees,
    localization,
    searchQuery,
    setSearchQuery,
    filterValues,
    setFilterValues,
    isFilterActive,
    numberOfActiveFilters,
    resetActivePage,
    resetSelectedRows,
    ...restTableProps,
  };
};

export default useEmployeesTable;
